import { Form } from "@/components/ui/form";
import { PropertyMainInfo } from "../../components/PropertyMainInfo";
import { PropertyLocation } from "../../components/PropertyLocation";
import { PropertyDetails } from "../../components/PropertyDetails";
import { PropertyFeatures } from "../../components/PropertyFeatures";
import { PropertyImages } from "@/modules/Properties/components/PropertyImages/PropertyImages";
import { PropertyDates } from "../../components/PropertyDates";
import { PropertyContacts } from "@/modules/Properties/pages/PropertyPage/components/PropertyContacts";

import { PropertyResponseDto, UpdatePropertyDto } from "@/common/api/datasources/Api";
import { UseFormReturn } from "react-hook-form";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { propertiesService } from "@/common/api/services/properties";
import { PageHeader } from "@/components/shared/PageHeader";
import { Building2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PropertyActions } from "../../components/PropertyActions";

interface OverviewTabProps {
    property: PropertyResponseDto;
    form: UseFormReturn<any>;
    updateProperty: (data: UpdatePropertyDto) => void;
    isUpdating: boolean;
    uploadImages: (params: { files: File[] }) => void;
    reorderImages: (imageOrders: { imageUUID: string; order: number }[]) => void;
    deleteImage: (imageUUID: string) => void;
    deleteAllImages: () => void;
    isUploading: boolean;
    isDeleting: boolean;
    isDeletingAll: boolean;
}

export const OverviewTab = ({
    property,
    form,
    updateProperty,
    isUpdating,
    uploadImages,
    reorderImages,
    deleteImage,
    deleteAllImages,
    isUploading,
    isDeleting,
    isDeletingAll,
}: OverviewTabProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);

    const { mutate: deleteProperty, isPending: isDeleting2 } = useMutation({
        mutationFn: () => propertiesService.deleteProperty(property.propertyUUID),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["properties"] });
            toast({
                title: t("properties.success.deleted") || "Нерухомість успішно видалена",
            });
            navigate('/home/properties');
        },
        onError: () => {
            toast({
                title: t("properties.errors.deleteError") || "Помилка під час видалення нерухомості",
                variant: "destructive",
            });
        },
    });

    const handleSubmit = (values: any) => {
        updateProperty(values);

        toast({
            title: t("properties.toast.updateSuccess.title") || "Властивість оновлено",
            description: t("properties.toast.updateSuccess.description") || "Властивість була успішно оновлена",
            variant: "default",
        });

        setIsEditing(false);
    };

    const handleCancel = () => {
        if (property) {
            form.reset({
                name: property.name,
                description: property.description || "",
                type: property.type,
                status: property.status,
                price: property.price,
                currency: "USD",
                address: property.address || "",
                city: property.city || "",
                state: property.state || "",
                stateAbbreviation: property.stateAbbreviation || "",
                country: property.country || "",
                countryAbbreviation: property.countryAbbreviation || "",
                postalCode: property.postalCode || "",
                area: property.area || 0,
                rooms: property.rooms || 0,
                bathrooms: property.bathrooms || 0,
                floor: property.floor || 0,
                totalFloors: property.totalFloors || 0,
                yearBuilt: property.yearBuilt || new Date().getFullYear(),
                features: property.features || [],
                geoLocation: property.geoLocation || null,
            });
        }
        setIsEditing(false);

        toast({
            title: t("properties.toast.cancelEdit.title") || "Зміни скасовано",
            description: t("properties.toast.cancelEdit.description") || "Всі зміни були скасовані",
            variant: "default",
        });
    };

    return (
        <div>
            <PageHeader
                icon={<Building2 />}
                title={t("properties.title")}
                description={t("properties.description")}
                actions={
                    <PropertyActions
                        isEditing={isEditing}
                        isUpdating={isUpdating}
                        isDeleting={isDeleting2}
                        onEdit={() => setIsEditing(true)}
                        onSave={form.handleSubmit(handleSubmit)}
                        onCancel={handleCancel}
                        onDelete={() => deleteProperty()}
                    />
                }
            />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
                            <PropertyMainInfo form={form} isEditing={isEditing} />
                            <PropertyDetails form={form} isEditing={isEditing} />
                            <PropertyFeatures form={form} isEditing={isEditing} />
                            <PropertyLocation form={form} isEditing={isEditing} />
                        </form>
                    </Form>
                </div>
                <div className="space-y-8">
                    <PropertyImages
                        images={property.images || []}
                        onUpload={(files) => uploadImages({ files })}
                        onReorder={reorderImages}
                        onDelete={deleteImage}
                        onDeleteAll={deleteAllImages}
                        isUploading={isUploading}
                        isDeleting={isDeleting}
                        isDeletingAll={isDeletingAll}
                    />

                    <PropertyContacts
                        property={property}
                    />
                    <PropertyDates property={property} />
                </div>
            </div>
        </div>
    );
}; 